import React, { useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import WhyCloudare from "../components/WhyCloudare";
import FaqSection from "../components/FaqSection";
import { CONTACT, canonicalFor } from "../config/site";
import { TAX_PAGES } from "../data/taxPages";
import "../styles/tax-page.css";

/**
 * TaxPage — single template for every /tax/{country} mandate page.
 * Pure document flow (no absolute positioning) so the overlapping-H2 bug
 * cannot recur. Reads content from data/taxPages.js.
 *
 * France additionally supports an EN/FR locale toggle.
 */
export default function TaxPage({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.country;
  const data = TAX_PAGES[slug];
  const [locale, setLocale] = useState("en");

  if (!data) return <Navigate to="/tax/global" replace />;

  const hasFr = Array.isArray(data.locales) && data.locales.includes("fr");
  const L = locale === "fr" && data.fr ? { ...data, ...data.fr } : data;

  const metaPath = `/tax/${slug}`;

  return (
    <React.Fragment>
      <Seo
        path={metaPath}
        title={`${data.hero.h1} | Cloudare`}
        description={data.hero.subhead}
        serviceName={`${data.hero.h1}`}
        breadcrumb={[
          { name: "Home", path: "/" },
          { name: "E-Invoicing", path: "/einvoicing-erp-integration" },
          { name: data.hero.h1, path: metaPath },
        ]}
      />
      <Navbar />

      {/* 1. Hero */}
      <section className="tax-hero">
        <div className="tax-hero__inner">
          <p className="tax-hero__eyebrow">{L.hero.eyebrow}</p>
          <h1 className="tax-hero__title">{L.hero.h1}</h1>
          <p className="tax-hero__subhead">{L.hero.subhead}</p>
          <div className="tax-hero__cta">
            <a
              className="btn-primary"
              href={CONTACT.calendly}
              target="_blank"
              rel="noopener noreferrer"
            >
              {locale === "fr" ? "Planifier un appel" : "Book a readiness call"}
            </a>
            <a className="btn-secondary" href="#faq">
              {locale === "fr" ? "Voir la FAQ" : "View FAQ"}
            </a>
          </div>

          {hasFr && (
            <div
              className="locale-toggle"
              role="group"
              aria-label="Language"
            >
              <button
                type="button"
                className={locale === "en" ? "is-active" : ""}
                onClick={() => setLocale("en")}
              >
                EN
              </button>
              <button
                type="button"
                className={locale === "fr" ? "is-active" : ""}
                onClick={() => setLocale("fr")}
              >
                FR
              </button>
            </div>
          )}
        </div>
      </section>

      {/* 2. Mandate snapshot */}
      <section className="tax-snapshot">
        <div className="tax-snapshot__grid">
          {L.snapshot.map((s) => (
            <div className="snapshot-card" key={s.label}>
              <span className="snapshot-card__label">{s.label}</span>
              <span className="snapshot-card__value">{s.value}</span>
            </div>
          ))}
        </div>
      </section>

      {/* 3. Why Cloudare */}
      <WhyCloudare
        title={L.whyTitle}
        lede={L.whyLede}
        features={L.features}
        elevated={true}
      />

      {/* 4. Engagement model (horizontal stepper, flow layout) */}
      <section className="tax-engagement">
        <header className="why-cloudare__header">
          <p className="why-cloudare__eyebrow">
            {locale === "fr" ? "Notre démarche" : "Engagement model"}
          </p>
          <h2 className="why-cloudare__title">
            {locale === "fr" ? "De la découverte à la mise en service" : "Discovery to go-live"}
          </h2>
        </header>
        <ol className="stepper">
          {[
            "Discovery",
            "Architecture",
            "Commercial",
            "Implementation",
            "UAT",
            "Go-Live",
            "Knowledge Transfer",
          ].map((step, i) => (
            <li className="stepper__item" key={step}>
              <span className="stepper__num">{i + 1}</span>
              <span className="stepper__label">{step}</span>
            </li>
          ))}
        </ol>
      </section>

      {/* 6. FAQ */}
      <div id="faq">
        <FaqSection
          eyebrow={
            locale === "fr" ? "Questions fréquentes" : "Frequently asked questions"
          }
          title={
            locale === "fr"
              ? "FAQ facturation électronique"
              : `${data.countryCode} e-invoicing FAQ`
          }
          faqs={L.faqs}
          emitJsonLd={true}
        />
      </div>

      {/* 7. CTA banner */}
      <section className="tax-cta-banner">
        <h2>
          {locale === "fr"
            ? "Parlez à un spécialiste"
            : `Talk to a ${data.countryCode} e-invoicing specialist`}
        </h2>
        <a
          className="btn-primary"
          href={CONTACT.calendly}
          target="_blank"
          rel="noopener noreferrer"
        >
          {locale === "fr" ? "Planifier un appel" : "Book a discovery call"}
        </a>
      </section>

      <Footer />
    </React.Fragment>
  );
}
